import React, { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useInsuranceStore } from 'src/store/insurance';
import { usePlan } from '@features/onboarding/hooks/usePlan';
import { AIService } from 'src/services/ai';
import { EstimationRequest } from 'src/services/interfaces/ai';
import { format, parseISO, addMonths, startOfMonth } from 'date-fns';
import { ApplicantsStep } from './components/ApplicantsStep';
import { HealthQuestionsStep } from './components/HealthQuestionsStep';
import { DEFAULT_TIER } from '@features/onboarding/constants';
import { useApplicationStore } from 'src/store/application';
import { PHQPlanListStep } from './components/PHQPlanListStep';
import { Form } from '@components/ui/form';

export const TesterForm = () => {
  const { plans, getPlans } = useInsuranceStore();
  const { tier } = useApplicationStore();
  const { calculateCoverageLevel, getPlanPrice } = usePlan();
  const [estimationResult, setEstimationResult] = useState<any>(null);
  const [submitMessage, setSubmitMessage] = useState<any | null>(null);
  const [previewData, setPreviewData] = useState<EstimationRequest | null>(
    null
  );
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Ref для отслеживания предыдущего значения coverage level
  const prevCoverageLevelRef = React.useRef<string>('Primary');

  // CSS для анимации
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .fade-in {
        animation: fadeIn 0.5s ease-in-out;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const getFirstDayOfNextMonth = React.useCallback(() => {
    const now = parseISO(format(new Date(), 'yyyy-MM-dd'));
    const nextMonth = addMonths(now, 1);
    const firstDay = startOfMonth(nextMonth);
    return format(firstDay, 'yyyy-MM-dd');
  }, []);

  // Загружаем планы при инициализации
  React.useEffect(() => {
    if (plans.length === 0) {
      getPlans();
    }
  }, [plans.length, getPlans]);

  const form = useForm<any>({
    // resolver: zodResolver(onboardingSchema) as any, // validation disabled
    defaultValues: {
      applicants: [
        {
          applicant: 'primary',
          firstName: '',
          lastName: '',
          midName: '',
          gender: undefined,
          householdIncome: undefined,
          dateOfBirth: {
            month: '',
            day: '',
            year: '',
          },
          height: {
            ft: undefined,
            in: undefined,
          },
          weight: undefined,
          nicotine: false,
        },
      ],
      medicalPlan: '',
      medicalTags: [],
      dentalPlan: '',
      visionPlan: '',
      planTags: {
        dental: [],
        vision: [],
      },
      // PHQ Plan List fields
      phqPlanId: '',
      phqCoverageLevel: 'Primary',
      // Скрытые поля
      tier: tier,
      applicantsIndexes: ['0', '1', '2', '3'], // Все возможные индексы
      healthQuestions: {
        initialQuestions: {
          treatment: false,
          pregnancy: false,
          invalid: false,
        },
        medicationQuestion: {
          medications: '',
        },
        surgeriesDiabetesHeartMental: {
          conditions: [],
        },
        coverageStartDate: {
          effectiveDate: getFirstDayOfNextMonth(), // Автоматически первый день следующего месяца
        },
        privacyDisclosure: {
          accepted: true, // Любая строка
          signature: 'John Doe', // Любая строка
        },
      },
    },
  });

  // Функция для обновления данных формы
  const updateFormData = React.useCallback(
    (newData: any) => {
      // Обновляем значения формы
      Object.keys(newData).forEach((key) => {
        form.setValue(key as any, newData[key]);
      });
    },
    [form]
  );

  // Реактивный просмотр всех значений формы и построение превью данных
  const formValues = useWatch({ control: form.control });
  React.useEffect(() => {
    // Проверяем, что formValues.applicants существует
    if (!formValues.applicants || formValues.applicants.length === 0) {
      return;
    }

    try {
      // Calculate coverage level based on applicants
      const applicants = formValues.applicants || [];
      let coverageLevel = 'Primary'; // Default to Primary

      const hasSpouse = applicants.some(
        (app: any) => app.applicant === 'spouse'
      );
      const hasDependents = applicants.some((app: any) =>
        app.applicant?.startsWith('dependent')
      );

      if (hasSpouse && hasDependents) {
        coverageLevel = 'Family';
      } else if (hasDependents) {
        coverageLevel = 'Primary+Dependents';
      } else if (hasSpouse) {
        coverageLevel = 'Primary+Spouse';
      }

      // Update form with calculated coverage level only if it changed
      const currentCoverageLevel = form.getValues('phqCoverageLevel');
      if (
        currentCoverageLevel !== coverageLevel &&
        prevCoverageLevelRef.current !== coverageLevel
      ) {
        form.setValue('phqCoverageLevel', coverageLevel);
        prevCoverageLevelRef.current = coverageLevel;
      }

      const updatedData = {
        ...formValues,
        phqCoverageLevel: coverageLevel, // Ensure phqCoverageLevel is included
        tier: formValues.tier || DEFAULT_TIER,
        applicantsIndexes: ['0', '1', '2', '3'],
        healthQuestions: {
          ...formValues.healthQuestions,
          initialQuestions: {
            treatment:
              formValues?.healthQuestions?.initialQuestions?.treatment || false,
            pregnancy:
              formValues?.healthQuestions?.initialQuestions?.pregnancy || false,
            invalid:
              formValues?.healthQuestions?.initialQuestions?.invalid || false,
          },
          coverageStartDate: {
            effectiveDate: getFirstDayOfNextMonth(),
          },
          privacyDisclosure: {
            accepted: true,
            signature: 'John Doe',
          },
        },
      } as any;
      const req = transformToEstimationRequest(updatedData, plans);
      setPreviewData(req);
    } catch (e) {
      setPreviewData(null);
    }
  }, [formValues.applicants, form, getFirstDayOfNextMonth, updateFormData]);

  // Функция для преобразования данных формы в EstimationRequest
  const transformToEstimationRequest = React.useCallback(
    (data: any, availablePlans: any[]): EstimationRequest => {
      // Получаем основного заявителя (primary applicant)
      const primaryApplicant = data.applicants?.find(
        (app: any) => app.applicant === 'primary'
      );

      if (!primaryApplicant) {
        throw new Error('Primary applicant is required');
      }

      // Преобразуем дату рождения
      const dob = `${primaryApplicant.dateOfBirth.year}-${primaryApplicant.dateOfBirth.month.padStart(2, '0')}-${primaryApplicant.dateOfBirth.day.padStart(2, '0')}`;

      // Преобразуем планы
      const resultPlans = [] as any[];
      const planIdNum = Number(data.phqPlanId);
      const coverageStr = data.phqCoverageLevel;

      if (!isNaN(planIdNum) && planIdNum > 0) {
        // Определяем coverage number на основе выбранного уровня покрытия
        let coverageNum = 0;
        if (coverageStr === 'Primary') coverageNum = 1;
        else if (coverageStr === 'Primary+Spouse') coverageNum = 2;
        else if (coverageStr === 'Primary+Dependents') coverageNum = 3;
        else if (coverageStr === 'Family') coverageNum = 4;

        // Находим план в глобальном списке планов (используем plans из store)
        const selectedPlan = availablePlans.find(
          (p: any) => p.uid === planIdNum || p.id === planIdNum
        );

        // Определяем priceId на основе coverage и tier
        let priceId: number | undefined;
        if (selectedPlan && selectedPlan.prices) {
          console.log('Selected plan:', selectedPlan);
          console.log('Available prices:', selectedPlan.prices);

          // Ищем цену нужного coverage
          const matchingPrice = selectedPlan.prices.find(
            (p: any) => p.tier === DEFAULT_TIER && p.coverage === coverageNum
          );
          if (matchingPrice) {
            priceId = matchingPrice.id;
            console.log(
              'Found matching price:',
              matchingPrice,
              'priceId:',
              priceId
            );
          } else {
            console.log(
              `No matching price found for ${DEFAULT_TIER} and coverage`,
              coverageNum
            );
          }
        } else {
          console.log('No plan selected or no prices available');
        }

        resultPlans.push({
          id: planIdNum,
          priceId: priceId,
        });
      }

      // ----- Medications -----
      const rawMeds =
        (data.healthQuestions?.takingMedicationsNow as any)?.medications ||
        (data.healthQuestions?.medicationQuestion as any)?.medications ||
        data.healthQuestions?.medDetails?.medications ||
        [];
      const medications = Array.isArray(rawMeds)
        ? rawMeds.map((med: any) => {
            // applicant field may be 'primary'|'spouse'|'dependent' or index string
            let applicantNumber = 0;
            const a = med.applicant;
            if (!isNaN(Number(a))) applicantNumber = Number(a);
            else if (a === 'spouse') applicantNumber = 1;
            else if (a?.startsWith('dependent')) applicantNumber = 2;
            else if (typeof a === 'number') applicantNumber = a;
            return {
              applicant: applicantNumber,
              name: med.name,
              rxcui: med.rxcui,
              dosage: med.dosage,
              frequency: med.frequency,
              frequencyDescription: med.frequencyDescription,
              description: med.description || '',
            };
          })
        : [];

      // ----- Issues -----
      const issuesConditionsArr =
        data.healthQuestions?.surgeriesDiabetesHeartMental?.conditions || [];
      const issueDetails =
        data.healthQuestions?.surgeriesDiabetesHeartMental?.details || {};
      const issues = Array.isArray(issuesConditionsArr)
        ? issuesConditionsArr
            .filter((c: any) => c.value !== 'none')
            .map((c: any) => {
              const conditionDetails = issueDetails[c.value] || '';
              return {
                key: c.value,
                details: conditionDetails
                  ? [
                      {
                        key: c.value,
                        description: conditionDetails,
                      },
                    ]
                  : [],
              };
            })
        : [];

      // ----- Conditions -----
      const otherConds =
        data.healthQuestions?.otherHealthConditions?.conditions || [];
      const conditionDetails =
        data.healthQuestions?.otherHealthConditions?.details || {};
      const conditions: any[] = Array.isArray(otherConds)
        ? otherConds
            .filter((c: any) => c.value !== 'none')
            .map((c: any) => ({
              key: c.value,
              description: conditionDetails[c.value] || '',
            }))
        : [];

      // Преобразуем PHQ данные
      const phq = {
        treatment: data.healthQuestions?.initialQuestions?.treatment || false,
        invalid: data.healthQuestions?.initialQuestions?.invalid || false,
        pregnancy: data.healthQuestions?.initialQuestions?.pregnancy || false,
        effectiveDate:
          data.healthQuestions?.coverageStartDate?.effectiveDate ||
          getFirstDayOfNextMonth(),
        disclaimer: data.healthQuestions?.privacyDisclosure?.accepted || false,
        signature:
          data.healthQuestions?.privacyDisclosure?.signature || 'John Doe',
        medications,
        issues,
        conditions,
      };

      // Преобразуем заявителей
      const applicants =
        data.applicants?.map((app: any, index: number) => ({
          applicant: index,
          firstName: app.firstName,
          lastName: app.lastName,
          midName: app.midName || '',
          phone: '', // Пока пустая строка, можно добавить поле позже
          gender: app.gender || 'male',
          dob,
          nicotine: app.nicotine || false,
          weight: app.weight || 0,
          heightFt: app.height?.ft || 0,
          heightIn: app.height?.in || 0,
        })) || [];

      return {
        uid: 'test-uid', // Можно генерировать уникальный ID
        applicants,
        plans: resultPlans,
        phq,
        income: primaryApplicant.householdIncome || 0,
        address: {
          address1: '',
          address2: '',
          city: '',
          state: '',
          zipcode: '',
        },
      };
    },
    [getFirstDayOfNextMonth]
  );

  const handleSubmit = React.useCallback(
    async (data: any) => {
      try {
        setIsSubmitting(true);

        // Убеждаемся, что скрытые поля заполнены
        const updatedData = {
          ...data,
          tier: DEFAULT_TIER,
          applicantsIndexes: ['0', '1', '2', '3'],
          priceId: data.priceId, // Передаем priceId
          healthQuestions: {
            ...data.healthQuestions,
            initialQuestions: {
              treatment:
                data.healthQuestions?.initialQuestions?.treatment || false,
              pregnancy:
                data.healthQuestions?.initialQuestions?.pregnancy || false,
              invalid: data.healthQuestions?.initialQuestions?.invalid || false,
            },
            coverageStartDate: {
              effectiveDate: getFirstDayOfNextMonth(),
            },
            privacyDisclosure: {
              accepted: true,
              signature: 'John Doe',
            },
          },
        };

        console.log('Form submitted:', updatedData);

        // Преобразуем данные в формат EstimationRequest
        const estimationRequest = transformToEstimationRequest(
          updatedData,
          plans
        );
        console.log('Estimation request:', estimationRequest);

        // Отправляем данные на runEstimation
        const estimationResponse =
          await AIService.runEstimation(estimationRequest);
        console.log('Estimation response:', estimationResponse);
        setEstimationResult(estimationResponse);
        setSubmitMessage(estimationResponse);

        // Автоматически скроллим к response через небольшую задержку
        setTimeout(() => {
          const responseElement = document.getElementById('api-response');
          if (responseElement) {
            responseElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });

            // Добавляем небольшой отступ сверху для лучшей видимости
            window.scrollBy({
              top: -50,
              behavior: 'smooth',
            });
          }
        }, 100);
      } catch (error) {
        console.error('Error submitting form:', error);
        setSubmitMessage('Error submitting form');
      } finally {
        setIsSubmitting(false);
      }
    },
    [plans, transformToEstimationRequest, getFirstDayOfNextMonth]
  );

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="min-h-screen bg-gray-50 p-4 overflow-x-auto"
      >
        {/* Form */}
        <div className="p-6 space-y-8">
          {/* Applicants Step */}
          <div className="border-b border-gray-200 pb-8">
            <ApplicantsStep
              form={form as any}
              formData={form.watch()}
              updateFormData={updateFormData}
            />
          </div>

          {/* PHQ Plan List Step */}
          <div className="border-b border-gray-200 pb-8">
            <PHQPlanListStep
              form={form as any}
              formData={form.watch()}
              updateFormData={updateFormData}
              plans={plans}
              calculateCoverageLevel={calculateCoverageLevel}
              getPlanPrice={getPlanPrice}
            />
          </div>

          {/* Dental and Vision Step */}
          {/* <div className="border-b border-gray-200 pb-8">
            <DentalAndVisionStep
              form={form as any}
              formData={form.watch()}
              updateFormData={updateFormData}
              plans={plans}
              calculateCoverageLevel={calculateCoverageLevel}
              getPlanPrice={getPlanPrice}
              sortPlans={sortPlans}
            />
          </div> */}

          {/* Health Questions Step */}
          <div className="border-b border-gray-200 pb-8">
            <HealthQuestionsStep
              form={form as any}
              formData={form.watch()}
              updateFormData={updateFormData}
            />
          </div>

          {/* Finish Step */}
          {/* <div className="pb-8">
            <FinishStep
              form={form as any}
              formData={form.watch()}
              updateFormData={updateFormData}
            />
          </div> */}
        </div>
        <div className="p-6 space-y-4">
          {/* Preview block */}
          <button
            type="button"
            onClick={() => setShowPreview((prev) => !prev)}
            className="w-full px-6 py-3 text-lg font-medium text-gray-700 bg-gray-200 border border-gray-300 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            {showPreview ? 'Hide Data' : 'Show Data'}
          </button>
          {showPreview && previewData && (
            <div className="mt-6 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h3 className="text-lg font-medium text-yellow-900 mb-4">
                Data to be sent to runEstimation:
              </h3>
              <pre className="text-xs text-yellow-800 bg-yellow-100 p-4 rounded overflow-auto max-h-96">
                {JSON.stringify(previewData, null, 2)}
              </pre>
            </div>
          )}
          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full px-6 py-3 text-lg font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
              isSubmitting
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Submitting...</span>
              </div>
            ) : (
              'Submit Application'
            )}
          </button>

          {/* Loading indicator */}
          {isSubmitting && (
            <div className="mt-4 text-center text-blue-600">
              <p className="text-sm">Processing your application...</p>
            </div>
          )}
          {submitMessage && (
            <div
              id="api-response"
              className={`mt-4 font-medium text-lg rounded fade-in ${
                submitMessage.status === 'accepted'
                  ? 'bg-green-100 text-green-800'
                  : submitMessage.status === 'rejected'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-blue-100 text-blue-800'
              }`}
            >
              <h3
                className={`text-lg font-medium mb-4 ${
                  submitMessage.status === 'accepted'
                    ? 'text-green-900'
                    : submitMessage.status === 'rejected'
                      ? 'text-red-900'
                      : 'text-blue-900'
                }`}
              >
                API Response:{' '}
                {submitMessage.status === 'accepted'
                  ? 'Accepted'
                  : submitMessage.status === 'rejected'
                    ? 'Rejected'
                    : 'Response'}
              </h3>
              <div
                className={`text-sm p-4 rounded ${
                  submitMessage.status === 'accepted'
                    ? 'text-green-800 bg-green-200'
                    : submitMessage.status === 'rejected'
                      ? 'text-red-800 bg-red-200'
                      : 'text-blue-800 bg-blue-200'
                }`}
              >
                <p>
                  <strong>Status:</strong> {submitMessage.status}
                </p>
                {submitMessage.details && (
                  <div className="mt-2">
                    <p>
                      <strong>DTQ:</strong>{' '}
                      {submitMessage.details.dtq ? 'Yes' : 'No'}
                    </p>
                    <p>
                      <strong>Reason:</strong> {submitMessage.details.reason}
                    </p>
                    {submitMessage.details.price_id && (
                      <p>
                        <strong>Price ID:</strong>{' '}
                        {submitMessage.details.price_id}
                      </p>
                    )}
                  </div>
                )}
                {submitMessage.results && submitMessage.results.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-medium mb-2">Results:</h4>
                    {submitMessage.results.map((result: any, index: number) => (
                      <div
                        key={index}
                        className={`text-xs p-2 rounded mb-2 ${
                          submitMessage.status === 'accepted'
                            ? 'bg-green-300'
                            : submitMessage.status === 'rejected'
                              ? 'bg-red-300'
                              : 'bg-blue-300'
                        }`}
                      >
                        <p>
                          <strong>Name:</strong> {result.name}
                        </p>
                        <p>
                          <strong>Type:</strong> {result.applicant_type}
                        </p>
                        <p>
                          <strong>Age:</strong> {result.age}
                        </p>
                        <p>
                          <strong>BMI:</strong> {result.bmi}
                        </p>
                        <p>
                          <strong>Tier:</strong> {result.tier}
                        </p>
                        <p>
                          <strong>RX Spend:</strong> ${result.rx_spend}
                        </p>
                        <p>
                          <strong>Message:</strong> {result.message}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </form>
    </Form>
  );
};
